import React, { useCallback, useEffect, useRef, useState } from 'react';
import { boundByCodePoints, maxLengthForCodePoints } from '../../utils/textBounds';

/**
 * useInlineRenameField — the ONE contract every inline rename field in the
 * thread rail keeps (2026-09-16, Miles: "wasn't able to add special characters
 * when editing the category for some reason … this needs to be editable the
 * thread summary renaming as well").
 *
 * Three fields rename things here — a section header, the sections manager and
 * a thread row — and all three were written the same way by hand: Enter and
 * blur commit, Escape cancels, `maxLength` bounds. Each carried the same three
 * ways a character you typed never lands, all of them invisible to the person
 * typing:
 *
 * 1. **A system palette takes focus.** The macOS Character Viewer (⌃⌘Space) and
 *    the emoji panel are separate windows, so opening one BLURS the field —
 *    which committed the half-typed name and closed the editor before the
 *    character could be inserted. That is the reported bug. A blur while the
 *    document no longer has focus is not a dismissal: keep editing, and put the
 *    caret back when the window returns.
 * 2. **A dead key or an IME is mid-composition.** `´` then `e` on a Spanish or
 *    US-International layout, and every CJK input: that Enter belongs to the
 *    composition, not to the form. Committing there drops the accented
 *    character and saves the raw one. (This platform ships in Spanish.)
 * 3. **`maxLength` counts UTF-16 units.** An emoji is two of them, so one
 *    sitting on the boundary was cut in half — a lone surrogate, which
 *    Firestore rejects as invalid UTF-8, so the rename failed silently. The
 *    bound is code points; see `utils/textBounds`.
 *
 * Every guard FAILS SAFE to today's behaviour when it cannot tell: a browser
 * that reports focus differently commits on blur exactly as before, and a name
 * with no astral characters takes the fast path untouched.
 *
 * The hook owns the draft so the commit always reads the latest value — a
 * composition ends and blurs in the same tick, and a commit closing over stale
 * state is how the composed character gets dropped on the way out.
 */
export interface UseInlineRenameFieldOptions {
  /** The value the field opens with (and resets to on cancel). */
  initial: string;
  /** Bound in CODE POINTS. */
  maxCodePoints: number;
  /** Enter, or a blur that is a real dismissal. Receives the current draft. */
  onCommit: (value: string) => void;
  /** Escape. */
  onCancel: () => void;
  /** Called on every edit — for a call site that clears its own "taken" notice. */
  onEdit?: () => void;
}

export interface InlineRenameFieldProps {
  ref: React.RefObject<HTMLInputElement>;
  value: string;
  maxLength: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onCompositionStart: () => void;
  onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement>) => void;
}

export interface UseInlineRenameFieldResult {
  value: string;
  /** Reset the draft from outside (the label changed while not editing). */
  setValue: (next: string) => void;
  /**
   * Arm the field for another commit after one was REFUSED and the call site
   * kept it open (a taken name, a confirm still to answer). Without it the
   * once-only guard that stops Enter-then-blur committing twice would also
   * stop the retry.
   */
  resetCommit: () => void;
  /** Put the caret back — a call site refocusing after its own refusal notice. */
  focus: () => void;
  inputProps: InlineRenameFieldProps;
}

/** The whole window lost focus — a system palette, not a click elsewhere on the page. */
function windowLostFocus(): boolean {
  try {
    return typeof document !== 'undefined'
      && typeof document.hasFocus === 'function'
      && !document.hasFocus();
  } catch {
    return false;
  }
}

/** 229 is the legacy "IME is handling this" keyCode, still emitted by Safari. */
function isComposingEvent(e: React.KeyboardEvent<HTMLInputElement>): boolean {
  const native = e.nativeEvent as KeyboardEvent | undefined;
  return !!native && (native.isComposing === true || native.keyCode === 229);
}

export function useInlineRenameField(
  { initial, maxCodePoints, onCommit, onCancel, onEdit }: UseInlineRenameFieldOptions,
): UseInlineRenameFieldResult {
  const [value, setValueState] = useState(initial);
  const valueRef = useRef(value);
  valueRef.current = value;
  const inputRef = useRef<HTMLInputElement>(null);
  const composingRef = useRef(false);
  const awaitingWindowRef = useRef(false);
  const doneRef = useRef(false);

  const setValue = useCallback((next: string) => {
    setValueState(next);
    valueRef.current = next;
  }, []);

  // The window came back from the palette: put the caret where it was, with the
  // draft untouched. Registered once; a no-op unless a blur armed it.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onFocus = () => {
      if (!awaitingWindowRef.current) return;
      awaitingWindowRef.current = false;
      inputRef.current?.focus();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const commit = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onCommit(valueRef.current);
  }, [onCommit]);

  const cancel = useCallback(() => {
    doneRef.current = true;
    onCancel();
  }, [onCancel]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Bound on the way in so nothing downstream ever sees a split pair. A
    // composition in flight is left alone — an IME's intermediate text is not
    // the name, and editing it under the cursor breaks the candidate window.
    const raw = e.target.value;
    setValue(composingRef.current ? raw : boundByCodePoints(raw, maxCodePoints));
    // Editing after a refusal is a new attempt.
    doneRef.current = false;
    onEdit?.();
  }, [maxCodePoints, onEdit, setValue]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // The rail's rows and headers are buttons; a keystroke here is never theirs.
    e.stopPropagation();
    // Mid-composition, Enter and Escape belong to the IME (Escape withdraws the
    // candidate, Enter accepts it) — the form gets neither.
    if (composingRef.current || isComposingEvent(e)) return;
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  }, [commit, cancel]);

  const onBlur = useCallback(() => {
    if (windowLostFocus()) {
      // A palette, another application, a devtools window: the person is still
      // editing. Come back to it.
      awaitingWindowRef.current = true;
      return;
    }
    commit();
  }, [commit]);

  const onCompositionStart = useCallback(() => { composingRef.current = true; }, []);
  const onCompositionEnd = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    composingRef.current = false;
    // The composed text arrives with this event on some engines and as a
    // separate input event on others; bound it here so both land the same way.
    const composed = (e.target as HTMLInputElement | null)?.value;
    if (typeof composed === 'string') setValue(boundByCodePoints(composed, maxCodePoints));
  }, [maxCodePoints, setValue]);

  const resetCommit = useCallback(() => { doneRef.current = false; }, []);
  const focus = useCallback(() => { inputRef.current?.focus(); }, []);

  return {
    value,
    setValue,
    resetCommit,
    focus,
    inputProps: {
      ref: inputRef,
      value,
      maxLength: maxLengthForCodePoints(maxCodePoints),
      onChange,
      onKeyDown,
      onBlur,
      onCompositionStart,
      onCompositionEnd,
    },
  };
}
